/**
 * Sector Race — the pure half: the four states, the ranks, the tail, and where
 * a horse stands on its track.
 *
 * A list of ETFs against SPY as Strength and Momentum (`qf/sector_race.py`,
 * approved mock `docs/sector-race-widget-mock.html`). The server sends every
 * period of the window at once; everything the widget draws at a scrubbed
 * period is read from it here, so a scrub never asks again.
 *
 * ```
 * Strength  = 100 · rs / EMA(rs, n)          rs = close / SPY close
 * Momentum  = 100 · Strength / Strength[t−m]
 * ```
 */
import type { RaceMember } from "../api/daily-brief";

export type RaceState = "gaining" | "fading" | "falling" | "closing";

/** The quadrant chips' order: Gaining, Fading, Falling back, Closing. */
export const RACE_STATES: readonly RaceState[] = ["gaining", "fading", "falling", "closing"];

export const STATE_LABEL: Record<RaceState, string> = {
  gaining: "Gaining",
  fading: "Fading",
  falling: "Falling back",
  closing: "Closing",
};

/**
 * The four states as theme tokens. There is no lime token, so Closing — the
 * mock's second, yellower green — is `positive` mixed halfway to `warn`.
 */
export const STATE_COLOR: Record<RaceState, string> = {
  gaining: "var(--qf-positive)",
  fading: "var(--qf-warn)",
  falling: "var(--qf-negative)",
  closing: "color-mix(in srgb, var(--qf-positive) 50%, var(--qf-warn))",
};

/** The tail chip cycles 8 → 13 → 4; 13 is the longest the server pads the window for. */
export const TAILS = [4, 8, 13] as const;
export type Tail = (typeof TAILS)[number];
export const DEFAULT_TAIL: Tail = 8;
export const MAX_TAIL = 13;

/** One play step. */
export const PLAY_MS = 650;

/** The track spans Strength 94–106; a horse outside it stands at the edge. */
export const TRACK_MIN = 94;
export const TRACK_MAX = 106;

const isNum = (v: number | null | undefined): v is number => typeof v === "number" && Number.isFinite(v);

/** Gaining ≥ 100 / ≥ 100, Fading ≥ 100 / < 100, Falling back < 100 / < 100, Closing < 100 / ≥ 100; null on a gap. */
export function stateOf(strength: number | null | undefined, momentum: number | null | undefined): RaceState | null {
  if (!isNum(strength) || !isNum(momentum)) return null;
  if (strength >= 100) return momentum >= 100 ? "gaining" : "fading";
  return momentum >= 100 ? "closing" : "falling";
}

/** A member's state at period `t`. */
export function stateAt(m: RaceMember, t: number): RaceState | null {
  return stateOf(m.strength[t], m.momentum[t]);
}

/**
 * Rank by Strength at period `t`, 1 = furthest ahead. A member with no
 * Strength there has no rank; ties keep the members' order.
 */
export function rankAt(members: readonly RaceMember[], t: number): Map<string, number> {
  const ranked = members
    .map((m, i) => ({ m, i, s: m.strength[t] }))
    .filter((x): x is { m: RaceMember; i: number; s: number } => isNum(x.s))
    .sort((a, b) => b.s - a.s || a.i - b.i);
  return new Map(ranked.map((x, k) => [x.m.symbol, k + 1]));
}

/** The first period of the tail ending at `t`. */
export const tailStart = (t: number, tail: number) => Math.max(0, t - tail);

/**
 * Places gained (+) or lost (−) over the tail: the rank at its start less the
 * rank now. Null when either end has no rank.
 */
export function deltaRank(members: readonly RaceMember[], symbol: string, t: number, tail: number): number | null {
  const now = rankAt(members, t).get(symbol);
  const then = rankAt(members, tailStart(t, tail)).get(symbol);
  return now === undefined || then === undefined ? null : then - now;
}

/** Periods in a row, ending at `t`, in the state it is in at `t`; 0 on a gap. */
export function periodsInState(m: RaceMember, t: number): number {
  const s = stateAt(m, t);
  if (!s) return 0;
  let n = 0;
  for (let k = t; k >= 0 && stateAt(m, k) === s; k--) n++;
  return n;
}

/** It changed state this period — the ring. Not after a gap: there is nothing to have changed from. */
export function changedState(m: RaceMember, t: number): boolean {
  if (t <= 0) return false;
  const now = stateAt(m, t), before = stateAt(m, t - 1);
  return !!now && !!before && now !== before;
}

/** A Strength as a percent across the track, clamped to its ends. */
export function horsePos(strength: number): number {
  const s = Math.min(TRACK_MAX, Math.max(TRACK_MIN, strength));
  return ((s - TRACK_MIN) / (TRACK_MAX - TRACK_MIN)) * 100;
}

/** The arrow's font size, px: 7 at Momentum 100, growing to 13 at 2.5 away. */
export function arrowSize(momentum: number): number {
  return 7 + Math.min(1, Math.abs(momentum - 100) / 2.5) * 6;
}

export interface Ghost {
  t: number;
  /** Percent across the track. */
  pos: number;
  /** Rising toward now. */
  opacity: number;
}

/** The last `tail` periods before `t`, oldest first, each where it stood; a gap draws no dot. */
export function ghosts(m: RaceMember, t: number, tail: number): Ghost[] {
  const start = tailStart(t, tail);
  const span = t - start;
  const out: Ghost[] = [];
  for (let k = start; k < t; k++) {
    const s = m.strength[k];
    if (!isNum(s)) continue;
    out.push({ t: k, pos: horsePos(s), opacity: Number((0.15 + (0.5 * (k - start)) / span).toFixed(2)) });
  }
  return out;
}

/** 8 → 13 → 4 → 8. */
export function nextTail(tail: number): Tail {
  return tail === 8 ? 13 : tail === 13 ? 4 : 8;
}

/** A chip click: isolate that state, or — clicking the only one shown — show them all again. */
export function toggleShown(shown: readonly RaceState[], state: RaceState): RaceState[] {
  return shown.length === 1 && shown[0] === state ? [...RACE_STATES] : [state];
}

/** How many members are in each state at `t`. */
export function countStates(members: readonly RaceMember[], t: number): Record<RaceState, number> {
  const out: Record<RaceState, number> = { gaining: 0, fading: 0, falling: 0, closing: 0 };
  for (const m of members) {
    const s = stateAt(m, t);
    if (s) out[s]++;
  }
  return out;
}

/** A stored `shown` setting, anything that is not a state dropped; nothing left is all four. */
export function shownStates(v: unknown): RaceState[] {
  const ok = Array.isArray(v) ? RACE_STATES.filter((s) => v.includes(s)) : [];
  return ok.length ? ok : [...RACE_STATES];
}
